<template>
  <div v-if="labels.length" class="h-container mt-6">
    <ul class="flex list-none flex-wrap gap-2">
      <li v-for="label in labels" :key="`label-${label.group}-${label.id}`">
        <button
          type="button"
          class="inline-flex items-center rounded-full border border-transparent bg-background-medium px-4 py-2 text-sm transition-colors hover:border-border"
          @click="emit('filterRemove', label)"
        >
          <span>
            {{ label.groupName }}: <span class="font-semibold">{{ label.name }}</span>
          </span>
          <Icon class="ml-1 size-4" name="close" />
        </button>
      </li>

      <li>
        <button
          type="button"
          class="inline-flex items-center rounded-full border border-transparent bg-background-medium px-4 py-2 text-sm font-semibold transition-colors hover:border-border"
          @click="emit('filterClear')"
        >
          {{ t['filter.clear']() }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { Filter } from '~/components/filter-bar/filter'
import * as t from '~/messages'

interface Choices {
  choices: number[]
}

interface Range {
  min: number
  max: number
}

type AppliedFilter = Choices | Range

const props = defineProps<{
  filters: Filter[]
  appliedFilters: Record<string, AppliedFilter>
}>()

type FilterRemoveCandidate = ChoiceRemoveCandidate | RangeRemoveCandidate

interface ChoiceRemoveCandidate {
  type: 'choice'
  group: number
  id: number
}

interface RangeRemoveCandidate {
  type: 'range'
  group: number
}

const emit = defineEmits<{
  filterRemove: [candidate: FilterRemoveCandidate]
  filterClear: []
}>()

const addPrefixSuffix = (value: number, prefix: string | null, suffix: string | null) => {
  let result = ''

  if (prefix) {
    result += prefix + ' '
  }

  result += value

  if (suffix) {
    result += ' ' + suffix
  }

  return result
}

interface FilterLabel {
  group: number
  groupName: string
  id: number
  name: string
  type: 'choice' | 'range'
}

const labels = computed<FilterLabel[]>(() => {
  return Object.entries(props.appliedFilters).flatMap(([filter, values]): FilterLabel | FilterLabel[] => {
    const filterGroup = props.filters.find((x) => x.id.toString() === filter)
    if (!filterGroup) {
      return []
    }

    if (filterGroup.type === 'choice' && 'choices' in values) {
      return filterGroup.properties.options
        .filter((x) => values.choices.includes(x.id))
        .map((choice) => ({
          group: filterGroup.id,
          groupName: filterGroup.name,
          id: choice.id,
          name: choice.name,
          type: 'choice',
        }))
    } else if (filterGroup.type === 'range' && !('choices' in values)) {
      const interval = values

      return {
        group: filterGroup.id,
        groupName: filterGroup.name,
        id: 0,
        name:
          interval.min === interval.max
            ? addPrefixSuffix(interval.min, filterGroup.properties.prefix, filterGroup.properties.suffix)
            : addPrefixSuffix(interval.min, filterGroup.properties.prefix, filterGroup.properties.suffix) +
              ' - ' +
              addPrefixSuffix(interval.max, filterGroup.properties.prefix, filterGroup.properties.suffix),
        type: 'range',
      }
    }

    return []
  })
})
</script>
